import { Button, Layout, Row, Space, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import RecordAudioModal from "../../../components/RecordAudioModal/RecordAudioModal";
import VoiceChatListRow from "../../../components/VoiceChatListRow/VoiceChatListRow";
import { quickFileService } from "../../../services/quickFile.service";
import { storageService } from "../../../services";
import { AppDispatch } from "../../../redux/store";
import { getQuickFileAlbumDetails } from "../../../redux/quickFile/quickFileSlice";
import "./index.less";

interface CreatorCreateAudioComponentProps {
  inlineMode?: boolean;
  onBack?: () => void;
  onCreated?: (audioFile: File) => void;
  albumId?: string;
}

const CreatorCreateAudioComponent = ({ inlineMode = false, onBack, onCreated, albumId }: CreatorCreateAudioComponentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{ albumId: string }>();
  const location = useLocation() as { state?: { albumName?: string; albumId?: string } };
  const albumName = location?.state?.albumName || t("new-album");
  const currentAlbumId = albumId || params.albumId || location?.state?.albumId;
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadedData, setLoadedData] = useState(0);
  const currentFileSizeRef = useRef<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedRows, setUploadedRows] = useState<Array<{ title: string; date: string; duration: number; audioUrl: string }>>([]);

  useEffect(() => {
    const loadAlbumFiles = async () => {
      if (currentAlbumId) {
        try {
          const albumData = await dispatch(getQuickFileAlbumDetails(currentAlbumId)).unwrap();
          if (albumData?.files && albumData.files.length > 0) {
            const rows = albumData.files.map((file: any) => ({
              title: file.title || file.name || "Untitled Audio",
              date: new Date(file.createdAt).toLocaleDateString(),
              duration: file.time || file.duration || 0,
              audioUrl: file.absolutePath || file.url || "",
            }));
            setUploadedRows(rows);
          }
        } catch (error) {
          console.error("Failed to load album files:", error);
        }
      }
    };

    loadAlbumFiles();
  }, [currentAlbumId, dispatch]);

  const handleBack = () => {
    if (inlineMode && onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Guard: only allow audio files, prevent selecting videos/images from photo library
      const lowerName = (file.name || "").toLowerCase();
      const isAudioMime = (file.type || "").startsWith("audio/");
      const isAudioExt = [".mp3", ".m4a", ".aac", ".wav", ".caf", ".aif", ".aiff", ".flac", ".ogg", ".m4b", ".webm"].some((ext) =>
        lowerName.endsWith(ext)
      );

      if (!(isAudioMime || isAudioExt)) {
        message.error(t("please-select-audio-file") || "Veuillez sélectionner un fichier audio");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (currentAlbumId) {
        await uploadAudioFile(file);
      } else if (onCreated) {
        onCreated(file);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAudioRecorded = async (audioBlob: Blob, audioTitle: string, duration: number) => {
    // Convert blob to file with the audio title as filename
    const audioFile = new File([audioBlob], `${audioTitle}.webm`, { type: audioBlob.type });

    // Close modal immediately so the uploaded row will be visible
    setIsRecordOpen(false);

    if (currentAlbumId) {
      await uploadAudioFile(audioFile, audioTitle, duration);
    } else if (onCreated) {
      onCreated(audioFile);
    }
  };

  const uploadAudioFile = async (file: File, explicitTitle?: string, explicitDuration?: number) => {
    if (!currentAlbumId) {
      message.error(t("no-album-selected") || "No album selected");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setLoadedData(0);
      currentFileSizeRef.current = file.size || 0;

      // Use explicit duration if provided, otherwise try to load from file
      let audioDuration = explicitDuration || 0;
      if (!explicitDuration) {
        try {
          audioDuration = await new Promise<number>((resolve, reject) => {
            const audio = new Audio();
            const url = URL.createObjectURL(file);
            audio.src = url;

            audio.onloadedmetadata = () => {
              const duration = Math.floor(audio.duration);
              URL.revokeObjectURL(url);
              resolve(duration);
            };

            audio.onerror = () => {
              URL.revokeObjectURL(url);
              reject(new Error("Failed to load audio metadata"));
            };
          });
        } catch (error) {
          console.warn("Could not load audio duration from file:", error);
        }
      }

      // Use explicit title if provided, otherwise extract from filename
      const audioTitle = explicitTitle || file.name.replace(/\.[^/.]+$/, "");

      // First upload the file to storage
      const audioFile = {
        name: file.name,
        type: file.type,
        size: file.size,
        originFileObj: file,
      };

      const result = await storageService.uploadFileMultiPart(audioFile, setLoadedData, file.size, "quickFiles");

      // Then add the metadata to the album
      const audioFiles: any[] = [];
      audioFiles.push({
        Location: result.Location,
        isPrivate: false,
        Key: result.Key,
        ETag: result.ETag,
        Bucket: result.Bucket,
        filename: file.name,
        size: file.size,
        title: audioTitle, // Use the explicit title here
        time: audioDuration,
      });

      const creatorId = localStorage.getItem("selectedCreator");
      const formData = new FormData();
      formData.append("audioFiles", JSON.stringify(audioFiles));
      formData.append("creatorId", creatorId as string);

      await quickFileService.addMediaToAlbum(currentAlbumId, formData, setUploadProgress);

      message.success(t("audio-upload-success") || "Audio uploaded successfully");

      // Immediately show the uploaded audio as a VoiceChatListRow with the correct title
      const displayedDate = new Date().toLocaleDateString();
      setUploadedRows((prev) => [
        {
          title: audioTitle, // Use the explicit title for immediate display
          date: displayedDate,
          duration: audioDuration,
          audioUrl: (result as any)?.Location || "",
        },
        ...prev,
      ]);

      navigate(`/creator/AlbumFeeds/${currentAlbumId}/audios`, {
        state: { albumName },
      });
    } catch (error: any) {
      console.error("Error uploading audio:", error);
      message.error(error?.message || t("audio-upload-failed") || "Failed to upload audio");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setLoadedData(0);
      currentFileSizeRef.current = 0;
    }
  };

  // Derive percentage from bytes loaded during multipart upload
  useEffect(() => {
    if (!isUploading) return;
    const total = currentFileSizeRef.current;
    if (total && total > 0) {
      const pct = Math.min(100, Math.round((loadedData / total) * 100));
      // Only update if we're still in the S3 stage; the API stage will set its own progress via setUploadProgress
      setUploadProgress(pct);
    }
  }, [loadedData, isUploading]);

  return (
    <Layout className={`gradient-background-dark-blue ${inlineMode ? "inline" : ""}`}>
      <Layout.Header className="header background-transparent">
        <Row className="relative justify-content-center">
          <ArrowLeftIconComponent top={10} callback={handleBack} />
          <Typography.Text className="header-title-black-35">{albumName as string}</Typography.Text>
        </Row>
      </Layout.Header>

      <Layout.Content className="content CreatorCreateAudioContentWrapper background-transparent">
        <Space className="w-full gap-21 mt-30" direction="vertical">
          {uploadedRows.length > 0 && (
            <div className="w-full" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {uploadedRows.map((row, idx) => (
                <VoiceChatListRow
                  key={row.audioUrl || `${row.title}-${row.date}-${row.duration}-${idx}`}
                  title={row.title}
                  date={row.date}
                  duration={row.duration}
                  audioUrl={row.audioUrl}
                />
              ))}
            </div>
          )}
          <Row className="w-full justify-content-center">
            <div className="instruction">{t("create-audio-instruction")}</div>
          </Row>

          <div className="bottom-actions">
            <Button
              className="authBtn audio-record-btn"
              onClick={() => setIsRecordOpen(true)}
              style={{ width: "80%", fontSize: 18, height: "60px", marginBottom: "16px" }}
              disabled={isUploading}
              loading={isUploading}
            >
              {isUploading ? `${t("audio-uploading")}... ${uploadProgress}%` : t("record-audio")}
            </Button>
            <button
              className="transparent-button"
              onClick={handleImportClick}
              disabled={isUploading}
              style={{ opacity: isUploading ? 0.5 : 1, cursor: isUploading ? "not-allowed" : "pointer" }}
            >
              {t("import-audio-file")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.m4a,.aac,.wav,.caf,.aif,.aiff,.flac,.ogg,.m4b"
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
          </div>
        </Space>

        <RecordAudioModal
          isOpen={isRecordOpen}
          onClose={() => setIsRecordOpen(false)}
          onAudioReady={handleAudioRecorded}
          isFromConv={false}
          onUploadSuccess={async () => {
            // When AudioRecorderComponent uploads directly, it doesn't tell us what was uploaded
            // So we need to refresh the album to get the latest files
            if (currentAlbumId) {
              try {
                const albumData = await dispatch(getQuickFileAlbumDetails(currentAlbumId)).unwrap();
                // Get the most recently added file (first in the list)
                if (albumData?.files && albumData.files.length > 0) {
                  const newestFile = albumData.files[0];
                  setUploadedRows((prev) => [
                    {
                      title: newestFile.title || newestFile.name || "Untitled Audio",
                      date: new Date(newestFile.createdAt).toLocaleDateString(),
                      duration: newestFile.time || newestFile.duration || 0,
                      audioUrl: newestFile.url || "",
                    },
                    ...prev,
                  ]);
                }
                navigate(`/creator/AlbumFeeds/${currentAlbumId}/audios`, {
                  state: { albumName },
                });
              } catch (error) {
                console.error("Failed to refresh album:", error);
              }
            }
            setIsRecordOpen(false);
          }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default CreatorCreateAudioComponent;
